/**
 * Template editor for the campaign wizard (card S3): pick one of the four
 * library sequences or None (write your own), fill the [slots] once, and edit
 * every message. What is saved is sent as written (card S1), with {variables}
 * filled per lead.
 */
import { HelpCircle } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { SequenceStep } from '../types'
import { VariableTextarea } from '../variable-textarea'
import { TEMPLATE_ANGLES } from './library'
import {
  applyAngle,
  BOOKING_LINK_SLOT,
  clearMessageSteps,
  findAngle,
  findSlots,
  isTemplateStep,
  NO_TEMPLATE,
  SENDER_SLOTS,
  slotValuesWithLink,
  stepLabel,
  stepMessageKeys,
  templateProblems,
  type TemplateChoice,
} from './template-fill'

const MAX_TEMPLATE_LEN = 1500
const MAX_SLOT_LEN = 200

/**
 * Example answers shown in each slot box. "[how you deliver it]" always follows
 * "We", so it needs a verb ("run...", not "an AI agent"), which the label alone
 * does not say. No numbers: an example result could be copied as a claim.
 */
const SLOT_EXAMPLES: Readonly<Record<string, string>> = {
  'who you help': 'e.g. founders of B2B agencies',
  'the problem you solve': 'e.g. not enough sales calls',
  'a second problem you solve': 'e.g. hours lost to manual follow-ups',
  'how you deliver it': 'Follows "We", e.g. run your LinkedIn outreach for you',
  'the result you deliver': 'e.g. a steady flow of booked calls',
  'the area you help with': 'e.g. outbound sales',
  'a real client result, or a short description of a typical project':
    'e.g. what you did for one real client, in a sentence or two',
  'your first name': 'Your own first name, e.g. Sam',
  'your company': 'Your own company, e.g. Northline Growth',
  'your title': 'Your own title, e.g. Founder',
}

/** The None card, shown after the four sequences. */
const NONE_OPTION = {
  name: 'None, I’ll write my own',
  when: 'Start with empty messages and write each one yourself. Nothing is pre-filled.',
}

/** Help behind the info icons (owner request 2026-10-20: "an information icon that explains how they can fill this out"). */
const PICK_HELP =
  'Pick the sequence closest to how you want to open, or None to write every message yourself. ' +
  'Then fill in the boxes once, and edit any message. ' +
  'Lead chips like {first_name} are filled with each lead’s details when the message is sent. ' +
  'You chips like [your first name] are your own details, written once and the same in every message.'
const FILL_HELP =
  'Each answer is dropped into the sentence where its [box] appears, so write it to read naturally there. ' +
  'The grey text shows the kind of answer that fits. ' +
  'Your booking link comes from the campaign’s Details step.'

function InfoIcon({ label, text }: { readonly label: string; readonly text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-muted-foreground hover:text-foreground"
          aria-label={label}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

/** True when a message step's text no longer matches the picked sequence (the user edited it). */
function hasEdits(steps: readonly SequenceStep[], angleId: TemplateChoice | null): boolean {
  const angle = findAngle(angleId)
  const keys = stepMessageKeys(steps)
  return steps.some((step, i) => {
    if (!isTemplateStep(step) || step.template === undefined) return false
    const key = keys[i]
    return !angle || !key || step.template !== angle.messages[key]
  })
}

export function TemplateEditor({
  steps,
  onStepsChange,
  angle,
  onAngleChange,
  slotValues,
  onSlotValuesChange,
  campaignLink,
}: {
  readonly steps: readonly SequenceStep[]
  readonly onStepsChange: (steps: readonly SequenceStep[]) => void
  readonly angle: TemplateChoice | null
  readonly onAngleChange: (angle: TemplateChoice) => void
  readonly slotValues: Readonly<Record<string, string>>
  readonly onSlotValuesChange: (values: Readonly<Record<string, string>>) => void
  /** The campaign's booking link from Details; it fills [your booking link]. */
  readonly campaignLink: string | null
}) {
  const allSlots = findSlots(steps.map((s) => s.template))
  // [your booking link] is filled from the campaign, never typed.
  const slots = allSlots.filter((s) => s !== BOOKING_LINK_SLOT)
  const usesBookingLink = allSlots.includes(BOOKING_LINK_SLOT)
  const problems = angle ? templateProblems(steps, slotValuesWithLink(slotValues, campaignLink)) : []
  const keys = stepMessageKeys(steps)
  const labelFor = (index: number) =>
    keys[index] === 'breakup' ? `${stepLabel(steps[index]!)} (break-up)` : stepLabel(steps[index]!)

  function pick(id: TemplateChoice) {
    if (id === angle) return
    const question = id === NO_TEMPLATE
      ? 'Start from empty messages? Your edits to the messages will be cleared.'
      : 'Switch sequence? Your edits to the messages will be replaced.'
    if (hasEdits(steps, angle) && !window.confirm(question)) return
    onAngleChange(id)
    onStepsChange(id === NO_TEMPLATE ? clearMessageSteps(steps) : applyAngle(steps, id))
  }

  const choices = [
    ...TEMPLATE_ANGLES.map((a) => ({ id: a.id as TemplateChoice, name: a.name, when: a.when })),
    { id: NO_TEMPLATE as TemplateChoice, ...NONE_OPTION },
  ]

  function editStep(index: number, template: string) {
    onStepsChange(steps.map((step, i) => (i === index ? { ...step, template } : step)))
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Label className="text-sm font-semibold">Pick a sequence</Label>
          <InfoIcon label="How do templates work?" text={PICK_HELP} />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {choices.map((a) => (
            <button
              key={a.id}
              type="button"
              aria-pressed={a.id === angle}
              onClick={() => pick(a.id)}
              className={[
                'rounded-lg border p-3 text-left transition-colors',
                a.id === angle ? 'border-cobalt-bright bg-primary/10' : 'hover:bg-muted/50',
              ].join(' ')}
            >
              <span className="block text-sm font-medium">{a.name}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{a.when}</span>
            </button>
          ))}
        </div>
      </div>

      {angle && slots.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border bg-card p-3">
          <div className="flex items-center gap-1">
            <Label className="text-sm font-semibold">Fill these in once</Label>
            <InfoIcon label="How do I fill these in?" text={FILL_HELP} />
          </div>
          <p className="text-xs text-muted-foreground">
            Each one goes into every message where it appears.
          </p>
          {slots.map((slot) => (
            <div key={slot} className="flex flex-col gap-1">
              <Label htmlFor={`slot-${slot}`} className="text-xs">[{slot}]</Label>
              <Input
                id={`slot-${slot}`}
                className="h-8 text-xs"
                value={slotValues[slot] ?? ''}
                placeholder={SLOT_EXAMPLES[slot]}
                maxLength={MAX_SLOT_LEN}
                onChange={(e) => onSlotValuesChange({ ...slotValues, [slot]: e.target.value })}
              />
            </div>
          ))}
        </div>
      )}

      {angle && usesBookingLink && campaignLink?.trim() && (
        <p className="text-xs text-muted-foreground">
          [{BOOKING_LINK_SLOT}] is your campaign&rsquo;s link from Details:{' '}
          <span className="break-all font-mono">{campaignLink.trim()}</span>
        </p>
      )}

      {angle && (
        <div className="flex flex-col gap-3">
          <Label className="text-sm font-semibold">Your messages</Label>
          <p className="text-xs text-muted-foreground">
            Edit any message. They are sent exactly as written, with the variables filled for each lead.
          </p>
          {steps.map((step, index) =>
            isTemplateStep(step) ? (
              <div key={index} className="flex flex-col gap-1">
                <Label className="text-xs">{labelFor(index)}</Label>
                <VariableTextarea
                  value={step.template ?? ''}
                  maxLength={MAX_TEMPLATE_LEN}
                  className="min-h-[150px] field-sizing-content text-sm"
                  ariaLabel={labelFor(index)}
                  senderSlots={SENDER_SLOTS}
                  onChange={(next) => editStep(index, next)}
                />
              </div>
            ) : null,
          )}
        </div>
      )}

      {problems.length > 0 && (
        <ul className="list-disc rounded-md border border-amber-500/30 bg-amber-500/5 py-2 pl-6 pr-3 text-xs text-amber-800 dark:text-amber-200">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
